import {
    Avatar,
    Button,
    Card,
    Content,
    Flex,
    Heading,
    HtmlButton,
    Modal,
    SearchField,
    Stack,
    Text
} from "@hopper-ui/components";
import { AngleRightIcon, CheckmarkIcon, PlusIcon } from "@hopper-ui/icons";
import type { SgWorkspace } from "../../../api/types.ts";
import { compareItems, rankItem } from "@tanstack/match-sorter-utils";
import { type PropsWithChildren, useMemo, useState } from "react";
import { useShellTranslation } from "../../../i18n/i18next.ts";
import { useWorkspaceContext } from "../../../WorkspaceContext.tsx";
import { cleanupDisplayName } from "./cleanupDisplayName.ts";

function SectionLabel({ children }: PropsWithChildren) {
    return (
        <Text
            fontFamily="overline"
            fontSize="overline"
            fontWeight="overline"
            lineHeight="overline"
            color="neutral-weak"
            textTransform="uppercase"
            data-public
        >
            {children}
        </Text>
    );
}

interface WorkspaceRowProps {
    workspace: SgWorkspace;
    isCurrent: boolean;
    onSwitch: (workspace: SgWorkspace) => void;
}

function WorkspaceRow({ workspace, isCurrent, onSwitch }: WorkspaceRowProps) {
    const { t } = useShellTranslation();

    return (
        <HtmlButton
            type="button"
            aria-label={
                isCurrent
                    ? t("WorkspaceSwitcherModal.currentWorkspace", { workspaceName: workspace.name })
                    : t("WorkspaceSwitcherModal.switchTo", { workspaceName: workspace.name })
            }
            width="100%"
            backgroundColor="transparent"
            color="inherit"
            border="none"
            padding="core_0"
            textAlign="left"
            cursor={isCurrent ? "default" : "pointer"}
            onClick={isCurrent ? undefined : () => onSwitch(workspace)}
            tabIndex={isCurrent ? -1 : undefined}
            aria-disabled={isCurrent ? true : undefined}
        >
            <Flex
                alignItems="center"
                gap="inline-md"
                padding="inset-md"
                backgroundColor={isCurrent ? "neutral-weak" : undefined}
                backgroundColorHover={isCurrent ? undefined : "neutral-hover"}
                borderRadius="rounded-md"
            >
                <Avatar name={cleanupDisplayName(workspace.name)} borderRadius="rounded-md" flexShrink={0} />
                <Text
                    size="sm"
                    fontWeight={isCurrent ? "body-sm-semibold" : undefined}
                    overflow="hidden"
                    textOverflow="ellipsis"
                    whiteSpace="nowrap"
                >
                    {workspace.name}
                </Text>
                {isCurrent ? (
                    <CheckmarkIcon size="sm" UNSAFE_marginLeft="auto" flexShrink={0} />
                ) : (
                    <AngleRightIcon size="sm" color="neutral-weak" UNSAFE_marginLeft="auto" flexShrink={0} />
                )}
            </Flex>
        </HtmlButton>
    );
}

function NoResultsView() {
    const { t } = useShellTranslation();

    return (
        <Card padding="inset-md">
            <Flex justifyContent="center" alignItems="center">
                <Text size="sm" color="neutral-weak" data-public>
                    {t("WorkspaceSwitcherModal.noResults")}
                </Text>
            </Flex>
        </Card>
    );
}

interface SearchResultsViewProps {
    workspaces: SgWorkspace[];
    currentWorkspaceId: string | undefined;
    onSwitch: (workspace: SgWorkspace) => void;
}

function SearchResultsView({ workspaces, currentWorkspaceId, onSwitch }: SearchResultsViewProps) {
    return (
        <Card overflowY="auto" flex={1} padding="inset-xs">
            <Stack gap="stack-xs">
                {workspaces.map(ws => (
                    <WorkspaceRow
                        key={ws.id}
                        workspace={ws}
                        isCurrent={ws.id === currentWorkspaceId}
                        onSwitch={onSwitch}
                    />
                ))}
            </Stack>
        </Card>
    );
}

interface ListViewProps {
    partnerWorkspaces: SgWorkspace[];
    clientWorkspaces: SgWorkspace[];
    currentWorkspaceId: string | undefined;
    onSwitch: (workspace: SgWorkspace) => void;
}

function ListView({ partnerWorkspaces, clientWorkspaces, currentWorkspaceId, onSwitch }: ListViewProps) {
    const hasPartner = partnerWorkspaces.length > 0;
    const hasClients = clientWorkspaces.length > 0;
    const { t } = useShellTranslation();

    return (
        <Stack gap="stack-md" flex={1} minHeight={0}>
            {hasPartner && (
                <Stack gap="stack-xs">
                    <SectionLabel>
                        {t("WorkspaceSwitcher.yourWorkspaces", { count: partnerWorkspaces.length })}
                    </SectionLabel>
                    {partnerWorkspaces.map(ws => (
                        <WorkspaceRow
                            key={ws.id}
                            workspace={ws}
                            isCurrent={ws.id === currentWorkspaceId}
                            onSwitch={onSwitch}
                        />
                    ))}
                </Stack>
            )}
            {hasClients && (
                <Stack gap="stack-xs" flex={1} minHeight={0}>
                    {hasPartner && <SectionLabel>{t("WorkspaceSwitcher.clientWorkspaces")}</SectionLabel>}
                    <Card overflowY="auto" flex={1} padding="inset-xs">
                        <Stack gap="stack-xs">
                            {clientWorkspaces.map(ws => (
                                <WorkspaceRow
                                    key={ws.id}
                                    workspace={ws}
                                    isCurrent={ws.id === currentWorkspaceId}
                                    onSwitch={onSwitch}
                                />
                            ))}
                        </Stack>
                    </Card>
                </Stack>
            )}
        </Stack>
    );
}

interface WorkspaceSwitcherModalContentProps {
    onSwitch: () => void;
}

function WorkspaceSwitcherModalContent({ onSwitch }: WorkspaceSwitcherModalContentProps) {
    const { t } = useShellTranslation();
    const [search, setSearch] = useState("");
    const { changeWorkspace, currentWorkspace, workspaces } = useWorkspaceContext();

    const sortedWorkspaces = useMemo(() => workspaces.toSorted((a, b) => a.name.localeCompare(b.name)), [workspaces]);

    const partnerWorkspaces = useMemo(() => sortedWorkspaces.filter(ws => ws.isPartner), [sortedWorkspaces]);

    const clientWorkspaces = useMemo(() => sortedWorkspaces.filter(ws => !ws.isPartner), [sortedWorkspaces]);

    const allMatchingWorkspaces = useMemo(
        () =>
            search
                ? sortedWorkspaces
                      .map(w => ({ ws: w, rank: rankItem(w.name, search) }))
                      .filter(({ rank }) => rank.passed)
                      .toSorted((a, b) => compareItems(a.rank, b.rank))
                      .map(({ ws }) => ws)
                : [],
        [sortedWorkspaces, search]
    );

    const hasNoResults = search !== "" && allMatchingWorkspaces.length === 0;
    const matchCount = allMatchingWorkspaces.length;
    const countLabel = search
        ? t("WorkspaceSwitcherModal.matchCount", { matchCount, count: workspaces.length })
        : t("WorkspaceSwitcherModal.count", { count: workspaces.length });

    const handleSwitch = (workspace: SgWorkspace) => {
        changeWorkspace(workspace);
        setSearch("");
        onSwitch();
    };

    return (
        <Content>
            <Stack gap="stack-md">
                <Text size="sm" color="neutral-weak" data-public>
                    {countLabel}
                </Text>
                <SearchField
                    aria-label={t("WorkspaceSwitcherModal.search.label")}
                    placeholder={t("WorkspaceSwitcherModal.search.placeholder")}
                    value={search}
                    onChange={setSearch}
                    isFluid
                    autoFocus
                />
                <Flex direction="column" UNSAFE_height="24rem">
                    {hasNoResults ? (
                        <NoResultsView />
                    ) : search ? (
                        <SearchResultsView
                            workspaces={allMatchingWorkspaces}
                            currentWorkspaceId={currentWorkspace?.id}
                            onSwitch={handleSwitch}
                        />
                    ) : (
                        <ListView
                            partnerWorkspaces={partnerWorkspaces}
                            clientWorkspaces={clientWorkspaces}
                            currentWorkspaceId={currentWorkspace?.id}
                            onSwitch={handleSwitch}
                        />
                    )}
                </Flex>
            </Stack>
        </Content>
    );
}

interface WorkspaceSwitcherModalProps {
    onCreateWorkspace?: () => void;
}

export function WorkspaceSwitcherModal({ onCreateWorkspace }: WorkspaceSwitcherModalProps) {
    const { t } = useShellTranslation();

    return (
        <Modal size="md">
            {({ close }) => (
                <>
                    <Heading data-public>{t("WorkspaceSwitcherModal.title")}</Heading>
                    <WorkspaceSwitcherModalContent onSwitch={close} />
                    {onCreateWorkspace && (
                        <Flex justifyContent="space-between" alignItems="center" width="100%" data-public>
                            <Button
                                variant="secondary"
                                onPress={() => {
                                    close();
                                    onCreateWorkspace();
                                }}
                            >
                                <PlusIcon />
                                <Text>{t("WorkspaceSwitcher.newWorkspace")}</Text>
                            </Button>
                            <Button variant="secondary" onPress={close}>
                                {t("WorkspaceSwitcherModal.cancel")}
                            </Button>
                        </Flex>
                    )}
                </>
            )}
        </Modal>
    );
}
